import { useParams, useNavigate } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { tripService, vehicleService, driverService, geofenceService } from "@/services/api";
import { MapCanvas, SpeedBandLegend } from "@/components/common/MapCanvas";
import { StatItem } from "@/components/common/Telemetry";
import { SkeletonMap } from "@/components/common/Skeletons";
import { EmptyState } from "@/components/common/EmptyState";
import { shortDate, fmtKm, fmtDuration, fmtL, fmtOdometer } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer } from "lucide-react";
import { TripTimeline } from "@/components/common/TripTimeline";
import { VehicleTripsPanel } from "@/components/common/VehicleTripsPanel";

// Shown under the closing reading as a cross-check: the odometer's own
// difference should agree with the distance Bouncie reported for the trip. When
// it does not, one of the two is wrong, and seeing both is the only way to
// notice. Nothing is shown unless both readings are present.
function odometerDelta(start, end) {
  if (start == null || end == null) return undefined;
  const delta = end - start;
  if (!Number.isFinite(delta)) return undefined;
  return `${delta >= 0 ? "+" : "−"}${Math.abs(delta).toFixed(1)} mi on the odometer`;
}

export default function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: t, loading } = useFetch(() => tripService.get(id), [id]);
  const { data: route } = useFetch(() => (id ? tripService.route(id) : Promise.resolve(null)), [id]);
  const { data: geofences } = useFetch(() => geofenceService.list(), []);
  // Events are their own call rather than part of the trip payload: they are a
  // join across alerts and only this view needs them.
  const { data: eventData } = useFetch(() => (id ? tripService.events(id) : Promise.resolve(null)), [id]);

  if (loading) return <div className="p-6"><SkeletonMap className="h-[420px]" /></div>;
  if (!t) return <div className="p-8"><EmptyState title="Trip not found" action="Back to Trips" onAction={() => navigate("/trips")} /></div>;

  const v = vehicleService.byId(t.vehicleId);
  const d = t.driverId ? driverService.byId(t.driverId) : null;

  // Real, derived-from-backend figures rather than the old hardcoded
  // "12% of duration" idle-time guess — idleSeconds and durationMin both
  // come straight from Bouncie's own recorded trip metrics.
  const idleMin = t.idleSeconds != null ? t.idleSeconds / 60 : null;
  const movingMin = idleMin != null ? Math.max(t.durationMin - idleMin, 0) : null;
  const efficiencyPct = movingMin != null && t.durationMin > 0 ? Math.round((movingMin / t.durationMin) * 100) : null;
  // distanceKm/fuelL are named for the unit the backend column used to be
  // documented as; both are actually miles/gallons (see lib/format.js), so
  // this division is already real-world miles-per-gallon, no conversion.
  const fuelEconomy = t.fuelL > 0 ? t.distanceKm / t.fuelL : null;

  return (
    <div className="p-4 md:p-6 lg:p-8 print:p-0">
      <button onClick={() => navigate("/trips")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground print:hidden"><ArrowLeft className="h-4 w-4" /> Trips</button>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">{t.startLoc} → {t.endLoc}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{v?.name} · {d?.name || "Unassigned"} · {shortDate(t.startTime, t.utcOffsetMinutes)}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={cn("inline-flex w-fit items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium", t.status === "in-progress" ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-700 dark:text-emerald-300" : "border-border/60 bg-secondary/40 text-muted-foreground")}>
            {t.status === "in-progress" ? "In progress" : "Completed"}
          </span>
          <Button variant="secondary" size="sm" className="print:hidden" onClick={() => window.print()} data-testid="print-trip">
            <Printer className="mr-1.5 h-4 w-4" /> Print
          </Button>
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-[1fr_360px] print:grid-cols-1">
        <div className="space-y-4">
          <div className="h-[420px] print:h-[320px]"><MapCanvas vehicles={v ? [v] : []} geofences={geofences || []} route={route} controls className="h-full" testId="trip-map" /></div>
          <SpeedBandLegend />
          {/* Between the map and the figures on purpose: the same speed bands
              colour the route above and the profile here, so the timeline reads
              as the map laid out along time. */}
          <TripTimeline
            trip={t}
            route={route}
            events={eventData?.events || []}
            counts={eventData?.counts}
          />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 print:grid-cols-4">
            <StatItem label="Distance" value={fmtKm(t.distanceKm)} />
            <StatItem label="Duration" value={fmtDuration(t.durationMin)} />
            <StatItem label="Fuel used" value={fmtL(t.fuelL)} />
            <StatItem label="Idle time" value={idleMin != null ? fmtDuration(Math.round(idleMin)) : "—"} />
            <StatItem label="Max speed" value={`${t.maxSpeed.toFixed(1)} mph`} />
            <StatItem label="Avg speed" value={`${t.avgSpeed} mph`} />
            <StatItem label="Fuel economy" value={fuelEconomy != null ? `${fuelEconomy.toFixed(1)} mpg` : "—"} />
            <StatItem label="Efficiency" value={efficiencyPct != null ? `${efficiencyPct}%` : "—"} sub={idleMin != null ? `${fmtDuration(Math.round(movingMin))} moving / ${fmtDuration(Math.round(idleMin))} idle` : undefined} />
            <StatItem label="Hard braking" value={t.hardBraking} />
            <StatItem label="Hard acceleration" value={t.hardAccel} />
            {/* Absolute readings from the vehicle, not distance arithmetic —
                these are the figures a service record or mileage claim has to
                match. The opening reading only arrives with REST
                reconciliation, so a just-ended trip can show one and not the
                other; each is dashed independently rather than hiding both. */}
            <StatItem label="Start odometer" value={fmtOdometer(t.startOdometer)} />
            <StatItem
              label="End odometer"
              value={fmtOdometer(t.endOdometer)}
              sub={odometerDelta(t.startOdometer, t.endOdometer)}
            />
          </div>
        </div>

        {/* Was the start/end record. That moved into the horizontal timeline
            under the map, where it can be read against the speed profile;
            this column is more useful for moving between a vehicle's trips.
            Height matched to the map + legend + timeline so the list scrolls
            inside the card instead of stretching the page. */}
        <VehicleTripsPanel
          initialVehicleId={t.vehicleId}
          currentTripId={t.id}
          className="h-[640px] print:hidden"
        />

      </div>
    </div>
  );
}
